import { useMemo, useState } from 'react';
import { useGetMtoDeficitQuery } from '@shared/api';
import type { DeficitRow } from '@entities/mto';
import { deficitKindLabel, deficitKindTone, fmtDate } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { Input, Label } from '@shared/ui/Input';
import { siteOrDash } from '../helpers';
import {
  DeterministicNote,
  Empty,
  FilterBar,
  FilterField,
  LotName,
  Muted,
  SectionTitle,
  Table,
  TableScroll,
  Wrap,
} from '../styles';

export function DeficitTab() {
  const [site, setSite] = useState('');
  const siteFilter = site.trim();
  const { data, isLoading } = useGetMtoDeficitQuery(siteFilter ? { site_id: siteFilter } : undefined);
  const rows = useMemo<DeficitRow[]>(() => data?.items ?? [], [data]);

  return (
    <Wrap>
      <FilterBar>
        <FilterField>
          <Label htmlFor="qc-deficit-site">Фильтр по объекту</Label>
          <Input
            id="qc-deficit-site"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder="Объект, например ДГП-2 (пусто — все)"
          />
        </FilterField>
      </FilterBar>
      <DeterministicNote>
        Дефицит — применённые на объекте партии, у которых на дату применения нет ни одного
        действительного документа качества. Тип дефицита определяется по точным правилам, без
        искусственного интеллекта.
      </DeterministicNote>

      <Card>
        <SectionTitle>
          Дефицит документов <Badge $tone={rows.length > 0 ? 'danger' : 'neutral'}>{rows.length}</Badge>
        </SectionTitle>
        {isLoading ? (
          <Muted>Загрузка отчёта…</Muted>
        ) : rows.length === 0 ? (
          <Empty>Дефицита не обнаружено.</Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Наименование / партия</th>
                  <th scope="col">Объект</th>
                  <th scope="col">Элемент</th>
                  <th scope="col">Дата применения</th>
                  <th scope="col" className="num">
                    Документов
                  </th>
                  <th scope="col">Дефицит</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.application.id}>
                    <td>
                      <LotName>{row.nomenclature ?? '—'}</LotName>
                      {row.lot_no && row.lot_no.trim() ? ` · ${row.lot_no}` : ''}
                    </td>
                    <td>{siteOrDash(row.application.site_id)}</td>
                    <td>{row.application.element_id ?? '—'}</td>
                    <td>{fmtDate(row.application.applied_date)}</td>
                    <td className="num">{row.doc_count}</td>
                    <td>
                      <Badge $tone={deficitKindTone(row.kind)}>{deficitKindLabel(row.kind)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Card>
    </Wrap>
  );
}
